import { useState, useRef, useEffect } from 'react';
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   FormHelperText,
   Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '../../Utils/RichEditor';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CustomDatePicker from '../../Components/MUI/CustomDatePicker';
import { useGetEstimateListQuery } from '../../Redux/Service/EstimateApi';
import { Formik, Form, Field, FieldArray } from 'formik';
import UploadLogo from '../../Assets/upload.svg';
import * as Yup from 'yup';
import {
   useUpdateQuotationMutation,
   useFindQuotationQuery,
   useGetQuotationStatusQuery,
} from '../../Redux/Service/QuotationApi';
import { toast } from 'react-toastify';
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi';
import Loading from '../../Components/Loading';
import { API_URL } from '../../Config';
import defaultSignature from '../../Assets/signature.png';
import { useGetopportunitiesQuery } from '../../Redux/Service/OpportunityApi';
import InfiniteAutocomplete from '../../Components/InfiniteAutocomplete';
import StyledDashboardLayout from '../style';
import ServiceProviderSection from './ServiceProviderSection';
import QuotationDetailFields, { QuotationDetailsHelp } from './QuotationDetailFields';
import {
   detailsSubTotal,
   emptyDetail,
   toFormRows,
   toPayloadRow,
   validateDetailCost,
} from '../../Utils/quotationDetails';

// Hours in one unit of each time type (One Time is handled by the util).
// Row cost and submit use 173.33 for months while the sub-total has always used 160;
// both are kept exactly as they were.
const hoursPerUnit = (type) =>
   type === 'Days' ? 8 : type === 'Months' ? 173.33 : type === 'Years' ? 2079.96 : 40;
const subTotalHoursPerUnit = (type) =>
   type === 'Days' ? 8 : type === 'Months' ? 160 : type === 'Years' ? 1920 : 40;

function ServiceProviderPrefill({ serviceProvider, values, setFieldValue }) {
   const appliedRef = useRef(false);

   useEffect(() => {
      if (appliedRef.current) return;

      if (!serviceProvider?.email && !serviceProvider?.phone) return;

      // Only fill if currently empty, so we never stomp a user's manual selection
      if (!values.serviceProviderEmail && serviceProvider.email) {
         setFieldValue('serviceProviderEmail', serviceProvider.email);
      }
      if (!values.serviceProviderPhone && serviceProvider.phone) {
         setFieldValue('serviceProviderPhone', serviceProvider.phone);
      }
      if (serviceProvider.id && !values.serviceProviderId) {
         setFieldValue('serviceProviderId', serviceProvider.id);
      }

      appliedRef.current = true;
   }, [
      serviceProvider,
      values.serviceProviderEmail,
      values.serviceProviderPhone,
      values.serviceProviderId,
      setFieldValue,
   ]);

   return null;
}

function QuotationEdit() {
   const [loading, setLoading] = useState(true);
   const [fileError, setFileError] = useState(null);
   // const [signatureSrc, setSignatureSrc] = useState(null);
   const { id } = useParams();
   const navigate = useNavigate();
   const [updateQuotation] = useUpdateQuotationMutation();
   const [imagePreview, setImagePreview] = useState(null);
   const signatureRef = useRef();
   const [symbol, setSymbol] = useState('$');
   const {
      data: quotationData,
      isLoading: isLoadingForQuotation,
      isError: isErrorForQuotation,
      error: errorForQuotation,
   } = useFindQuotationQuery(id);
   const [opportunity, setOpportunity] = useState({ name: '' }); // Removed phone
   const [serviceProvider, setServiceProvider] = useState({
      name: '',
      email: '',
      phone: '',
   });
   const [serviceProviders, setServiceProviders] = useState([]);
   const {
      data: estimateData,
      isLoading: isLoadingForEstimate,
      isError: isErrorForEstimate,
      error: errorForEstimate,
   } = useGetEstimateListQuery();
   const {
      data: currencies,
      isLoading: isLoadingForCurrency,
      error: errorForCurrency,
   } = useGetCurrenciesQuery();
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const debounceTimeout = useRef(null);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const {
      data: opportunityData,
      isLoading: isLoadingForOpportunity,
      isError: isErrorForOpportunity,
      error: errorForOpportunity,
   } = useGetopportunitiesQuery({ limit: 10, name: search, page });

   useEffect(() => {
      if (quotationData?.response?.clientAccepted) {
         toast.info('Quotation has already been accepted and cannot be modified.');
         navigate('/quotation');
      }
   }, [quotationData, navigate]);

   useEffect(() => {
      if (isLoadingForQuotation) return;
      // Fetch full Service Provider list and set default based on quotationData if present
      const fetchServiceProviders = async () => {
         try {
            const response = await fetch(`${API_URL}service`);
            const data = await response.json();

            if (data?.statusCode === 200 && Array.isArray(data.response)) {
               setServiceProviders(data.response);

               // If quotation data is available, try to find matching provider from the list and set it
               const q = quotationData?.response;

               if (q) {
                  // Try to find provider by id from quotation or estimate
                  const qProvId = q.serviceProvider?.id || q.estimate?.serviceProvider?.id;
                  let matched;
                  if (qProvId) matched = data.response.find((p) => p.id === qProvId);

                  // Fallback: match by name if id not available
                  if (!matched && q.serviceProviderName) {
                     matched = data.response.find((p) => p.name === q.serviceProviderName);
                  }

                  if (matched) {
                     setServiceProvider({
                        name: matched.name || '',
                        email: matched.email || '',
                        phone: matched.phoneNo || '',
                        id: matched.id,
                     });
                  } else {
                     const first = data.response[0];
                     if (first && !q.serviceProviderName) {
                        setServiceProvider({
                           name: first.name || '',
                           email: first.email || '',
                           phone: first.phoneNo || '',
                           id: first.id,
                        });
                     }
                  }
               } else {
                  // No quotation data yet: set first provider as default
                  const first = data.response[0];
                  if (first) {
                     setServiceProvider({
                        name: first.name || '',
                        email: first.email || '',
                        phone: first.phoneNo || '',
                        id: first.id,
                     });
                  }
               }
            }
         } catch (error) {
            console.error('Failed to fetch Service Provider details:', error);
         }
      };

      fetchServiceProviders();
   }, [quotationData, isLoadingForQuotation]);

   useEffect(() => {
      if (!isLoadingForQuotation && !isErrorForQuotation && quotationData) {
         const estimateCurrency = quotationData?.response?.estimate?.currency;
         const quotationCurrency = quotationData?.response?.currency;

         // Set opportunity data
         if (quotationData?.response?.opportunity) {
            console.log('Opportunity data found:', quotationData.response.opportunity);
            setSelectedOption(quotationData.response.opportunity);
            setOpportunity({
               name: quotationData.response.opportunity.name || '',
            });
         }

         // Set service provider data if available in the quotation
         if (quotationData?.response?.estimate?.serviceProvider) {
            setServiceProvider({
               name: quotationData?.response?.estimate?.serviceProvider?.name || '',
               email: quotationData?.response?.estimate?.serviceProvider?.email || '',
               phone: quotationData?.response?.estimate?.serviceProvider?.phoneNo || '',
            });
         }

         setSymbol(quotationCurrency?.symbol || estimateCurrency?.symbol || '$');
         setLoading(false);
      }
   }, [quotationData, isLoadingForQuotation, isErrorForQuotation]);

   useEffect(() => {
      // If we have a selected opportunity from the quotation, make sure it's in the options
      if (selectedOption && selectedOption.id) {
         setVisibleOptions((prevOptions) => {
            const existingIds = new Set(prevOptions.map((opt) => opt.id));

            // If the selected opportunity is not in the list, add it
            if (!existingIds.has(selectedOption.id)) {
               console.log('Adding selected opportunity to options:', selectedOption);
               return [selectedOption, ...prevOptions];
            }

            return prevOptions;
         });
      }

      // Add new options from the API response
      if (opportunityData?.response?.data) {
         const newData = opportunityData.response.data;

         setVisibleOptions((prevOptions) => {
            const existingIds = new Set(prevOptions.map((opt) => opt.id));
            const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
            return [...prevOptions, ...uniqueNewData];
         });
      }
   }, [opportunityData?.response?.data, selectedOption]);

   const handleScroll = (event) => {
      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   const removeSignature = (setFieldValue) => {
      signatureRef.current.value = null;
      setImagePreview(null);
      setFieldValue('signature', null);
   };

   const handleSelectOption = (event, value, reason, setFieldValue, setFieldTouched) => {
      setSelectedOption(value);
      setFieldValue('opportunity', reason === 'selectOption' ? value?.id : null);

      // if (reason === "clear") {
      //     setFieldTouched("opportunity", true);
      //     setOpportunity({ name: '' });
      // } else if (value) {
      //     setOpportunity({
      //         name: value.account || value.name || '',
      //     });
      // }
   };

   const handleSearch = (event, value) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
      }, 300);
   };

   const emailRegex = /^(?!.*\.\.)(?!.*@@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

   const validationSchema = Yup.object().shape({
      estimateId: Yup.string(), // Removed the required validation
      place: Yup.string()
         .required('Place is required')
         .matches(/^[A-Za-z\s]+$/, 'Place can contain only letters and spaces'),
      clientName: Yup.string()
         .required('Client Name is required'),
      serviceProviderPhone: Yup.string()
         .required('Service Provider Phone is required'),
      serviceProviderEmail: Yup.string()
         // .email('Invalid email')
         .test('valid-email', 'Please enter a valid email address', (value) => {
            if (!value) return true;

            // Additional checks
            if (value.startsWith('.')) return false;
            if (value.endsWith('.')) return false;
            if (value.includes('..')) return false;

            return emailRegex.test(value);
         })
         .required('Service Provider Email is required'),
      clientEmail: Yup.string()
         // .email('Invalid email')
         .test('valid-email', 'Please enter a valid email address', (value) => {
            if (!value) return true;

            // Additional checks
            if (value.startsWith('.')) return false;
            if (value.endsWith('.')) return false;
            if (value.includes('..')) return false;

            return emailRegex.test(value);
         })
         .test(
            'email-or-phone',
            'Client email or phone number is required',
            (value, ctx) => !!(value?.trim() || ctx.parent.clientPhone?.trim()),
         ),
      clientPhone: Yup.string(),
      description: Yup.string()
         .required('Description is required'),
      note: Yup.string(),
      thankYouMessage: Yup.string()
         .required('Thank You Message is required'),
      validTimeDay: Yup.number()
         .required('Valid time in days is required')
         .min(1, 'Valid For (Days) must be 1 or more')
         .max(30, 'Valid For (Days) should be at most 30 days'),

      gstType: Yup.string().required('GST Type is required'),
      // gstPercentage: Yup.number().min(0, 'GST Percentage cannot be a negative number'),
      title: Yup.string()
         .required('Title is required'),
      date: Yup.date().required('Date is required'),
      opportunity: Yup.number().required('Opportunity is required'),
      currencyId: Yup.number().required('Currency is required'),
      quotationDetails: Yup.array().of(
         Yup.object()
            .shape({
               description: Yup.string()
                  .required('Description is required')
                  .max(250, 'Description should be at most 250 characters'),
               type: Yup.string(),
               developmentTime: Yup.number()
                  .nullable()
                  .positive('Development Time must be a positive number')
                  .integer('Developement Time must be a whole number')
                  .max(9999, 'Development Time should be at most 9999'),
               chargePerHour: Yup.number()
                  .nullable()
                  .positive('Charge Per Hour must be a positive number')
                  .max(99999, 'Charge Per Hour should be at most 99999'),
               total: Yup.number().nullable().min(0, 'Cost cannot be negative'),
            })
            .test('detail-cost', function (row) {
               const problem = validateDetailCost(row);
               return problem
                  ? this.createError({
                       path: `${this.path}.${problem.field}`,
                       message: problem.message,
                    })
                  : true;
            }),
      ),
   });

   const handleSubTotal = (values) =>
      detailsSubTotal(values?.quotationDetails, subTotalHoursPerUnit);

   const gstTotal = (values) => {
      const gst = values.gstType !== 'none' ? 18 : 0;
      const gstPercentage = Number(gst / 100);
      const gstTotal = Number(handleSubTotal(values) * gstPercentage);
      return gstTotal.toFixed(2);
   };

   const allTotal = (values) => {
      const alltotal = Number(Number(gstTotal(values)) + Number(handleSubTotal(values)));
      return alltotal.toFixed(2);
   };

   const isInr = (values) =>
      currencies?.response?.find((val) => Number(val.id) === Number(values.currencyId))?.code === 'INR';

   const currencySymbolSet = (currencyId, setFieldValue) => {
      const id = Number(currencyId);
      setFieldValue('currencyId', id);
      const selectedCurrency = currencies?.response?.find((val) => Number(val.id) === id);
      if (selectedCurrency?.code !== 'INR') setFieldValue('gstType', 'none');
      setSymbol(selectedCurrency?.symbol ?? '$');
   };

   const handleEstimatesOnChange = (e, setFieldValue) => {
      const estimate = estimateData?.response?.find((val) => e.target.value === val.id);
      if (estimate) {
         // Update service provider fields from the estimate
         if (estimate.serviceProvider) {
            setServiceProvider({
               name: estimate.serviceProvider?.name || '_',
               email: estimate.serviceProvider?.email || '_',
               phone: estimate.serviceProvider?.phoneNo || '_',
            });
            setFieldValue('serviceProviderName', estimate.serviceProvider?.name || '_');
         }

         setFieldValue('estimateId', e.target.value);
      }
   };

   // Keep the local list in sync after the "Change" button saves a provider
   const handleProviderSaved = (updated) =>
      setServiceProviders((list) =>
         list.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)),
      );

   const handleSubmit = async (values) => {
      try {
         values.userId = 1;
         // ensure numeric conversions
         if (values.currencyId) values.currencyId = parseInt(values.currencyId);
         if (values.opportunity) values.opportunity = parseInt(values.opportunity);
         if (values.serviceProviderId)
            values.serviceProviderId = parseInt(values.serviceProviderId);
         if (values.estimateId === '' || values.estimateId === null) delete values.estimateId;
         values.serviceProviderName =
            serviceProviders.find((p) => p.id === values.serviceProviderId)?.name ??
            serviceProvider.name;
         // values.serviceProviderEmail = serviceProvider.email;
         // values.serviceProviderPhone = serviceProvider.phone;
         // quotationDetails already structured; remove any transient fields
         if (Array.isArray(values.quotationDetails)) {
            values.quotationDetails = values.quotationDetails.map((row) =>
               toPayloadRow(row, hoursPerUnit),
            );
         }
         // Include pre-calculated totals from frontend
         // values.subTotal = handleSubTotal(values);
         // values.gstTotal = Number(gstTotal(values));
         // values.total = Number(allTotal(values));
         // Pass values directly; RTK Query mutation should detect File and build FormData (same as create page behavior)
         const res = await updateQuotation(values);
         if (res?.error) {
            toast.error(res.error?.data?.message || 'Update failed');
         } else {
            toast.success(res.data?.message || 'Quotation updated');
            navigate('/quotation');
         }
      } catch (e) {
         console.error(e);
         toast.error('Something went wrong, Please try again later!');
      }
   };

   const handleRemoveItem = (id, arrayHelpers) => {
      arrayHelpers.remove(id);
   };

   const onImageChange = (event, setFieldValue) => {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

      if (!allowedTypes.includes(file.type)) {
         toast.error('Only JPG, PNG files are allowed');
         setFileError('Invalid file type. Only JPG, PNG files are allowed');
         event.target.value = '';
         setFieldValue('signature', null);
         setImagePreview(null);
         return;
      }
      setFileError(null);
      setFieldValue('signature', file);
      setImagePreview(URL.createObjectURL(file));
   };

   if (loading) {
      return <Loading />;
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Edit Quotation</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/quotation" className="btn addBtn">
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>

            <div className="card customCard p-3 m-3">
               {/* <div className="card-header">
                        <h3 className="card-title">Quotation</h3>
                    </div> */}
               {isLoadingForQuotation || isLoadingForOpportunity ? (
                  <div>Loading...</div>
               ) : errorForQuotation || errorForOpportunity ? (
                  <div>
                     Error: {errorForQuotation?.message}
                     {errorForOpportunity?.message}
                  </div>
               ) : (
                  <Formik
                     enableReinitialize={false}
                     initialValues={{
                        id: quotationData?.response?.id,
                        estimateId: quotationData?.response?.estimate?.id || '',
                        userId: quotationData?.response?.userId || '',
                        clientName: quotationData?.response?.clientName || '',
                        clientEmail: quotationData?.response?.clientEmail || '',
                        clientPhone: quotationData?.response?.clientPhone || '',
                        place: quotationData?.response?.place || '',
                        description: quotationData?.response?.description || '',
                        note: quotationData?.response?.note || '',
                        thankYouMessage: quotationData?.response?.thankYouMessage || '',
                        validTimeDay: quotationData?.response?.validTimeDay || '',
                        // gstPercentage: quotationData?.response?.gstPercentage || 0,
                        gstType: quotationData?.response?.gstType ?? 'none',
                        date: quotationData?.response?.date
                           ? new Date(quotationData.response.date)
                           : new Date(),
                        quotationDetails: toFormRows(quotationData?.response?.quotationDetails),
                        currencyId: quotationData?.response?.currency?.id || 1,
                        quotationStatusId: quotationData?.response?.quotationStatus?.id || 1,
                        signature: quotationData?.response?.signature || '',
                        title: quotationData?.response?.title || '',
                        opportunity: quotationData?.response?.opportunity?.id || '',
                        serviceProviderId:
                           quotationData?.response?.serviceProvider?.id ||
                           quotationData?.response?.estimate?.serviceProvider?.id ||
                           serviceProvider.id ||
                           '',
                        serviceProviderName:
                           quotationData?.response?.serviceProvider?.name ||
                           quotationData?.response?.estimate?.serviceProvider?.name ||
                           serviceProvider.name ||
                           '',
                        serviceProviderEmail:
                           quotationData?.response?.serviceProvider?.email ||
                           quotationData?.response?.estimate?.serviceProvider?.email ||
                           serviceProvider.email ||
                           '',
                        serviceProviderPhone:
                           quotationData?.response?.serviceProvider?.phoneNo ||
                           quotationData?.response?.estimate?.serviceProvider?.phoneNo ||
                           serviceProvider.phone ||
                           '',
                     }}
                     validationSchema={validationSchema}
                     onSubmit={(values) => handleSubmit(values)}
                  >
                     {({
                        isSubmitting,
                        values,
                        setFieldValue,
                        setFieldTouched,
                        touched,
                        errors,
                        handleBlur,
                     }) => {
                        console.log(
                           `[${Date.now()}] RENDER → id: ${values.serviceProviderId} | name: ${values.serviceProviderName}`,
                        );

                        let signatureFilename = null;
                        if (values.signature) {
                           if (values.signature instanceof File) {
                              // New file selected - use filename from File object
                              signatureFilename = values.signature.name;
                           } else if (typeof values.signature === 'string') {
                              // Prefilled string path - extract filename
                              signatureFilename = values.signature.includes('/')
                                 ? values.signature.split('/').pop()
                                 : values.signature;
                           }
                        }

                        // Compute signature source with image preview first, then signature
                        let signatureSrc = null;
                        if (imagePreview) {
                           signatureSrc = imagePreview;
                        } else if (typeof values.signature === 'string' && values.signature) {
                           const parts = values.signature.split('/');
                           signatureSrc = `${API_URL}signature/${parts[parts.length - 1]}`;
                        }

                        return (
                           <Form>
                              <ServiceProviderPrefill
                                 serviceProvider={serviceProvider}
                                 values={values}
                                 setFieldValue={setFieldValue}
                              />
                              <Box className="card-body" sx={{ padding: '15px 0' }}>
                                 <Grid container spacing={2}>
                                    {/* ── Quotation Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Quotation
                                          </Typography>
                                          <Grid container spacing={4}>
                                             <Grid item md={6} xs={12}>
                                                <label htmlFor="date">Date</label>
                                                <Field
                                                   component={CustomDatePicker}
                                                   label="Date"
                                                   name="date"
                                                   value={values.date}
                                                   onChange={(date) => setFieldValue('date', date)}
                                                   fullWidth
                                                />
                                             </Grid>

                                             <Grid item md={6} xs={12}>
                                                <label htmlFor="validTimeDay">
                                                   Valid For (Days)
                                                </label>
                                                <Field
                                                   fullWidth
                                                   label="Valid For (Days)"
                                                   name="validTimeDay"
                                                   variant="outlined"
                                                   type="number"
                                                   as={TextField}
                                                   error={
                                                      touched.validTimeDay &&
                                                      Boolean(errors.validTimeDay)
                                                   }
                                                   helperText={
                                                      touched.validTimeDay && errors.validTimeDay
                                                   }
                                                />
                                             </Grid>

                                             <Grid item md={6} xs={12}>
                                                <FormControl fullWidth>
                                                   <label htmlFor="opportunity">Opportunity</label>
                                                   <InfiniteAutocomplete
                                                      onBlur={handleBlur}
                                                      value={selectedOption}
                                                      onChange={(event, value, reason) =>
                                                         handleSelectOption(
                                                            event,
                                                            value,
                                                            reason,
                                                            setFieldValue,
                                                            setFieldTouched,
                                                         )
                                                      }
                                                      options={visibleOptions}
                                                      onScroll={handleScroll}
                                                      inputValue={search}
                                                      setInputValue={handleSearch}
                                                      total_options={'Opportunity'}
                                                      error={Boolean(
                                                         touched.opportunityId &&
                                                         errors.opportunityId,
                                                      )}
                                                      helperText={
                                                         touched.opportunityId &&
                                                         errors.opportunityId
                                                      }
                                                   />
                                                </FormControl>
                                             </Grid>

                                             <Grid item md={6} xs={12}>
                                                <label htmlFor="title">Quotation Title</label>
                                                <Field
                                                   fullWidth
                                                   label="Quotation Title"
                                                   name="title"
                                                   variant="outlined"
                                                   type="text"
                                                   as={TextField}
                                                   error={touched.title && Boolean(errors.title)}
                                                   helperText={touched.title && errors.title}
                                                />
                                             </Grid>

                                             <Grid item md={6} xs={12}>
                                                {isLoadingForEstimate ? (
                                                   <div>Loading...</div>
                                                ) : isErrorForEstimate ? (
                                                   <div>Error: {errorForEstimate?.message}</div>
                                                ) : (
                                                   <>
                                                      <label htmlFor="estimateId">
                                                         Estimates (Optional)
                                                      </label>
                                                      <FormControl fullWidth>
                                                         <InputLabel id="demo-select-small-label">
                                                            Estimates
                                                         </InputLabel>
                                                         <Field
                                                            label="Estimates"
                                                            name="estimateId"
                                                            as={Select}
                                                            labelId="demo-select-small-label"
                                                            id="demo-simple-select"
                                                            onChange={(event) => {
                                                               handleEstimatesOnChange(
                                                                  event,
                                                                  setFieldValue,
                                                               );
                                                            }}
                                                         >
                                                            {estimateData?.response?.map(
                                                               (val, index) => (
                                                                  <MenuItem
                                                                     key={index}
                                                                     value={val.id}
                                                                  >
                                                                     {val.name}
                                                                  </MenuItem>
                                                               ),
                                                            )}
                                                         </Field>
                                                      </FormControl>
                                                   </>
                                                )}
                                             </Grid>

                                             <Grid item md={6} xs={12}>
                                                <label htmlFor="currencyId">Currency</label>
                                                <FormControl
                                                   fullWidth
                                                   error={Boolean(
                                                      touched.currencyId && errors.currencyId,
                                                   )}
                                                >
                                                   <InputLabel id="currency-label">
                                                      Currency
                                                   </InputLabel>
                                                   <Field
                                                      label="Currency"
                                                      name="currencyId"
                                                      as={Select}
                                                      labelId="currency-label"
                                                      onChange={(event) =>
                                                         currencySymbolSet(
                                                            event.target.value,
                                                            setFieldValue,
                                                         )
                                                      }
                                                      onBlur={() =>
                                                         setFieldTouched('currencyId', true)
                                                      }
                                                      error={Boolean(
                                                         touched.currencyId && errors.currencyId,
                                                      )}
                                                   >
                                                      {currencies?.response?.map((val) => (
                                                         <MenuItem key={val.id} value={val.id}>
                                                            {val.name} ({val.symbol})
                                                         </MenuItem>
                                                      ))}
                                                   </Field>
                                                   {touched.currencyId && errors.currencyId && (
                                                      <FormHelperText>
                                                         {errors.currencyId}
                                                      </FormHelperText>
                                                   )}
                                                </FormControl>
                                                {/* aria-label and helper text */}
                                             </Grid>
                                          </Grid>
                                       </Box>
                                    </Grid>

                                    {/* ── Service Provider Section ── */}
                                    <Grid item xs={12}>
                                       <ServiceProviderSection
                                          serviceProviders={serviceProviders}
                                          onProviderSaved={handleProviderSaved}
                                       />
                                    </Grid>

                                    {/* ── Client Details Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Client Details
                                          </Typography>
                                          <Grid container spacing={4}>
                                             <Grid item md={4} xs={12}>
                                                <label htmlFor="clientName">Name</label>
                                                <Field
                                                   fullWidth
                                                   label="Name"
                                                   name="clientName"
                                                   variant="outlined"
                                                   as={TextField}
                                                   error={
                                                      touched.clientName &&
                                                      Boolean(errors.clientName)
                                                   }
                                                   helperText={
                                                      touched.clientName && errors.clientName
                                                   }
                                                />
                                             </Grid>
                                             <Grid item md={4} xs={12}>
                                                <label htmlFor="clientEmail">Email</label>
                                                <Field
                                                   fullWidth
                                                   label="Email"
                                                   name="clientEmail"
                                                   variant="outlined"
                                                   as={TextField}
                                                   error={
                                                      touched.clientEmail &&
                                                      Boolean(errors.clientEmail)
                                                   }
                                                   helperText={
                                                      touched.clientEmail && errors.clientEmail
                                                   }
                                                />
                                             </Grid>
                                             <Grid item md={4} xs={12}>
                                                <label htmlFor="clientPhone">Phone Number</label>
                                                <Field
                                                   fullWidth
                                                   label="Phone Number"
                                                   name="clientPhone"
                                                   variant="outlined"
                                                   as={TextField}
                                                   error={
                                                      touched.clientPhone &&
                                                      Boolean(errors.clientPhone)
                                                   }
                                                   helperText={
                                                      touched.clientPhone && errors.clientPhone
                                                   }
                                                />
                                             </Grid>
                                          </Grid>
                                       </Box>
                                    </Grid>

                                    {/* ── Project Description Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Project Description
                                          </Typography>
                                          <Grid container spacing={2}>
                                             <Grid item xs={12}>
                                                <Field name="description">
                                                   {({ field }) => (
                                                      <CKEditor
                                                         editor={ClassicEditor}
                                                         data={field.value}
                                                         onChange={(event, editor) =>
                                                            setFieldValue(
                                                               'description',
                                                               editor.getData(),
                                                            )
                                                         }
                                                         onError={
                                                            touched.description &&
                                                            errors.description
                                                         }
                                                      />
                                                   )}
                                                </Field>
                                                {/* <ErrorMessage
                                                   name="description"
                                                   component="div"
                                                   style={{ color: 'red' }}
                                                /> */}
                                                {touched.description && (
                                                   <FormHelperText
                                                      sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                   >
                                                      {touched.description && errors.description}
                                                   </FormHelperText>
                                                )}
                                             </Grid>
                                          </Grid>
                                       </Box>
                                    </Grid>

                                    {/* ── Development Time & Cost Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Development Time & Cost Details
                                          </Typography>
                                          {/* <QuotationDetailsHelp /> */}
                                          <FieldArray name="quotationDetails">
                                             {(arrayHelpers) => (
                                                <div>
                                                   {values.quotationDetails.map((val, index) => (
                                                      <Grid
                                                         container
                                                         spacing={2}
                                                         key={val.id}
                                                         sx={{ my: 1 }}
                                                         alignItems="flex-start"
                                                      >
                                                         <QuotationDetailFields
                                                            index={index}
                                                            symbol={symbol}
                                                            hoursPerUnit={hoursPerUnit}
                                                         />

                                                         {index !== 0 && (
                                                            <Grid
                                                               item
                                                               xs={12}
                                                               sx={{
                                                                  display: 'flex',
                                                                  justifyContent: 'flex-end',
                                                               }}
                                                            >
                                                               <Box
                                                                  sx={{
                                                                     display: 'flex',
                                                                     justifyContent: 'flex-end',
                                                                     alignItems: 'center',
                                                                  }}
                                                               >
                                                                  <DeleteIcon
                                                                     style={{ cursor: 'pointer' }}
                                                                     onClick={() =>
                                                                        handleRemoveItem(
                                                                           index,
                                                                           arrayHelpers,
                                                                        )
                                                                     }
                                                                  />
                                                               </Box>
                                                            </Grid>
                                                         )}
                                                      </Grid>
                                                   ))}

                                                   <Box sx={{ mt: 2 }}>
                                                      <button
                                                         type="button"
                                                         className="btn addBtn"
                                                         onClick={() =>
                                                            arrayHelpers.push(
                                                               emptyDetail(Date.now()),
                                                            )
                                                         }
                                                      >
                                                         <AddIcon /> Add Item
                                                      </button>
                                                   </Box>
                                                </div>
                                             )}
                                          </FieldArray>

                                          {/* <Grid container spacing={2} sx={{ mt: 4 }}>
                                             <Grid item xs={12} sx={{ my: 2 }}>
                                                <Grid container spacing={2}>
                                                   <Grid item xs={8}></Grid>
                                                   <Grid item xs={2}>
                                                      <h4>Sub Total :</h4>
                                                   </Grid>
                                                   <Grid item xs={2}>
                                                      <Box
                                                         sx={{
                                                            border: '1px solid grey',
                                                            borderRadius: '5px',
                                                            p: 2,
                                                         }}
                                                      >
                                                         <b>({symbol})</b> {handleSubTotal(values)}
                                                      </Box>
                                                   </Grid>
                                                </Grid>
                                             </Grid>
                                          </Grid> */}
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' },
                                                gap: '10px',
                                                mb: '10px',
                                             }}
                                          >
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   flexDirection: 'column',
                                                   alignItems: 'start',
                                                   gap: '10px',
                                                }}
                                             >
                                                {signatureSrc && (
                                                   <Box
                                                      sx={{
                                                         border: '1px solid grey',
                                                         p: 1,
                                                         width: '180px',
                                                      }}
                                                   >
                                                      <img
                                                         src={signatureSrc}
                                                         alt="signature"
                                                         style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                         }}
                                                         onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = defaultSignature;
                                                         }}
                                                      />
                                                   </Box>
                                                )}
                                                {values.signature && (
                                                   <button
                                                      type="button"
                                                      onClick={() => removeSignature(setFieldValue)}
                                                      style={{
                                                         padding: '5px 10px',
                                                         border: 'none',
                                                         fontSize: '14px',
                                                         backgroundColor: 'red',
                                                         color: '#fff',
                                                         borderRadius: '7px',
                                                      }}
                                                   >
                                                      Remove signature
                                                   </button>
                                                )}
                                             </Box>
                                             {isInr(values) && (
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   justifyContent: 'end',
                                                   gap: '10px',
                                                   mb: '10px',
                                                }}
                                             >
                                                <h4 style={{ margin: 0 }}>GST (%):</h4>
                                                <FormControl
                                                   sx={{ minWidth: { xs: 0, sm: 250 }, flex: { xs: 1, sm: 'none' } }}
                                                   error={
                                                      touched.gstType && Boolean(errors.gstType)
                                                   }
                                                >
                                                   <InputLabel id="gst-label">GST</InputLabel>
                                                   <Select
                                                      labelId="gst-label"
                                                      label="GST"
                                                      name="gstType"
                                                      value={values.gstType}
                                                      onChange={(e) =>
                                                         setFieldValue('gstType', e.target.value)
                                                      }
                                                   >
                                                      <MenuItem selected value="none">
                                                         N/A
                                                      </MenuItem>
                                                      <MenuItem value="cgst_sgst">
                                                         CGST(9%) + SGST(9%)
                                                      </MenuItem>
                                                      <MenuItem value="igst">IGST(18%)</MenuItem>
                                                   </Select>
                                                   {touched.gstType && errors.gstType && (
                                                      <FormHelperText>
                                                         {errors.gstType}
                                                      </FormHelperText>
                                                   )}
                                                </FormControl>
                                             </Box>
                                             )}
                                          </Box>

                                          {/* Signature + GST row */}
                                          <Box
                                             container
                                             spacing={2}
                                             sx={{ mt: 2 }}
                                             alignItems="center"
                                          >
                                             <Paper
                                                elevation={2}
                                                className="uploadFile"
                                                sx={{ mt: 1 }}
                                             >
                                                <Box>
                                                   <img
                                                      src={UploadLogo}
                                                      alt="Upload Logo"
                                                      className="uploadLogo"
                                                   />
                                                   <Typography variant="body2" sx={{ mt: 1 }}>
                                                      Drag and Drop or{' '}
                                                      <Link
                                                         component="button"
                                                         variant="body2"
                                                         className="chooseFile"
                                                         onClick={() =>
                                                            document
                                                               .getElementById('signature')
                                                               .click()
                                                         }
                                                      >
                                                         Choose Signature
                                                      </Link>{' '}
                                                      to upload
                                                   </Typography>
                                                   <Typography
                                                      sx={{
                                                         fontSize: '.95rem',
                                                         color: 'green',
                                                         fontWeight: 400,
                                                      }}
                                                   >
                                                      *Only jpg, jpeg, and png files
                                                   </Typography>
                                                   {fileError && (
                                                      <Typography
                                                         sx={{
                                                            fontSize: '.95rem',
                                                            color: 'red',
                                                            fontWeight: 400,
                                                         }}
                                                      >
                                                         {fileError}
                                                      </Typography>
                                                   )}
                                                </Box>

                                                {/* Hidden native input — still does the actual work, just visually hidden now */}
                                                <input
                                                   ref={signatureRef}
                                                   id="signature"
                                                   name="signature"
                                                   type="file"
                                                   accept="image/*"
                                                   style={{ display: 'none' }} // ADDED: hides native input so only the styled box shows
                                                   onChange={(event) =>
                                                      onImageChange(event, setFieldValue)
                                                   }
                                                />
                                             </Paper>
                                          </Box>

                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: { xs: 'space-between', sm: 'end' },
                                                flexWrap: 'wrap',
                                                gap: '10px',
                                                mb: '10px',
                                             }}
                                          >
                                             <h4>Total(Including GST Only) :</h4>
                                             <Box
                                                sx={{
                                                   border: '1px solid grey',
                                                   borderRadius: '5px',
                                                   p: 2,
                                                }}
                                             >
                                                <b>({symbol})</b> {allTotal(values)}
                                             </Box>
                                             {/* <Grid item xs={1}>
                                                   <Box
                                                      sx={{
                                                         border: '1px solid grey',
                                                         borderRadius: '5px',
                                                         p: 2,
                                                      }}
                                                   >
                                                      <b>({symbol})</b> {gstTotal(values)}
                                                   </Box>
                                                </Grid> */}
                                          </Box>
                                       </Box>
                                    </Grid>

                                    {/* ── Note Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Note
                                          </Typography>
                                          <Grid container spacing={2}>
                                             <Grid item xs={12}>
                                                <Field
                                                   name="note"
                                                   onError={touched.note && errors.note}
                                                >
                                                   {({ field }) => (
                                                      <CKEditor
                                                         editor={ClassicEditor}
                                                         data={field.value}
                                                         onChange={(event, editor) =>
                                                            setFieldValue('note', editor.getData())
                                                         }
                                                      />
                                                   )}
                                                </Field>
                                                {/* <ErrorMessage
                                                   name="note"
                                                   component="div"
                                                   style={{ color: 'red' }}
                                                /> */}
                                                {touched.note && (
                                                   <FormHelperText
                                                      sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                   >
                                                      {touched.note && errors.note}
                                                   </FormHelperText>
                                                )}
                                             </Grid>
                                          </Grid>
                                       </Box>
                                    </Grid>

                                    {/* ── Thank You Section ── */}
                                    <Grid item xs={12}>
                                       <Box className="commonBox">
                                          <Typography variant="h6" gutterBottom>
                                             Thank You for Your Business!
                                          </Typography>
                                          <Grid container spacing={4}>
                                             <Grid item xs={12}>
                                                <Field
                                                   name="thankYouMessage"
                                                   error={
                                                      touched.thankYouMessage &&
                                                      errors.thankYouMessage
                                                   }
                                                >
                                                   {({ field }) => (
                                                      <CKEditor
                                                         editor={ClassicEditor}
                                                         data={field.value}
                                                         config={{
                                                            placeholder:
                                                               'Enter thank you message here...',
                                                         }}
                                                         onChange={(event, editor) =>
                                                            setFieldValue(
                                                               'thankYouMessage',
                                                               editor.getData(),
                                                            )
                                                         }
                                                      />
                                                   )}
                                                </Field>
                                                {/* <ErrorMessage
                                                   name="thankYouMessage"
                                                   component="div"
                                                   style={{ color: 'red' }}
                                                /> */}
                                                {touched.thankYouMessage && (
                                                   <FormHelperText
                                                      sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                                   >
                                                      {touched.thankYouMessage &&
                                                         errors.thankYouMessage}
                                                   </FormHelperText>
                                                )}
                                             </Grid>
                                          </Grid>
                                       </Box>
                                    </Grid>
                                 </Grid>

                                 <Box sx={{ mt: 3 }}>
                                    <button
                                       type="submit"
                                       className="btn commonBtn mt-2"
                                       disabled={isSubmitting}
                                    >
                                       {isSubmitting ? 'Updating...' : 'Update'}
                                    </button>
                                 </Box>
                              </Box>
                           </Form>
                        );
                     }}
                  </Formik>
               )}
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default QuotationEdit;
